<template>
	<div class="grid-flex-container pa-4">
		<div style="flex-grow: 1">
			<v-tabs v-model="activeTab">
				<v-tab>
					<v-icon small class="mr-2">mdi-folder-cog-outline</v-icon>
					General
				</v-tab>
				<v-tab>
					<v-icon small class="mr-2">mdi-cellphone-lock</v-icon>
					Iris Mobile
				</v-tab>
			</v-tabs>
			<v-form ref="form">
				<v-tabs-items v-model="activeTab">
					<v-tab-item class="pt-4">
						<v-text-field v-model="formProject.Name"
									  name="Name"
									  label="Name"
									  :rules="nameRules"
									  required />
						<v-text-field v-if="formProject.Id && canViewProjectId"
									  v-model="formProject.Id"
									  name="Id"
									  label="Project ID"
									  disabled />
						<v-text-field v-model="formProject.Description"
									  name="Description"
									  label="Description"
									  required />
						<v-row v-if="formProject.Id">
							<v-col style="
									background-color: #ffc00030;
									display: flex;
									justify-content: flex-end;
									align-content: space-between;
								">
								<div v-if="formProject.DbConnection"
									 style="display: flex; align-items: center">
									<p style="font-size: 80%; margin-bottom: 0">
										<b>
											Server Name:
											{{ formProject.DbConnection.SqlServerName }}
											; Database Name:
											{{ formProject.DbConnection.DatabaseName }}
										</b>
									</p>
								</div>
								<div v-if="formProject.DbConnection == null"
									 style="display: flex; align-items: center">
									<p style="font-size: 80%; margin-bottom: 0">
										<b>There is no DbConnection defined yet</b>
									</p>
								</div>

								<v-spacer />
								<v-btn v-if="formProject.Id && showDbButton"
									   color="primary"
									   @click="dbConnectionSetup()">
									<v-icon class="mr-2">mdi-content-save</v-icon>
									DbConnectionSetup
								</v-btn>
							</v-col>
						</v-row>
						<v-row v-if="formProject.Id">
							<v-col>
								<v-btn color="primary"
									   class="mr-2 mt-2"
									   @click="createIntegrationApiKey()">Create Integration Api Key</v-btn>
								<v-text-field readonly
											  v-model="formProject.IntegrationApiKey"
											  placeholder="No Integration Api Key Created"></v-text-field>
							</v-col>
						</v-row>
						<v-row v-if="formProject.Id && canViewSwaggerUrl">
							<v-col>
								<v-text-field readonly
											  v-model="swaggerUrl"
											  label="Swagger URL for Mobile Module"></v-text-field>
							</v-col>
						</v-row>
						<v-row v-if="formProject.Id && canViewOdataUrl">
							<v-col>
								<v-text-field readonly
											  v-model="odataUrl"
											  label="Project Odata URL"></v-text-field>
							</v-col>
						</v-row>
						<v-row no-gutters v-if="formProject.Logo">
							<v-img class="my-2"
								   v-if="formProject.Logo"
								   :src="getProjectLogoURL(formProject.Logo)"
								   max-height="200"
								   max-width="200" />
							<v-btn class="action-btn"
								   fab
								   small
								   icon
								   elevation="1"
								   color="error"
								   @click.stop="deleteProjectLogo">
								<v-icon small> mdi-delete</v-icon>
							</v-btn>
						</v-row>
						<v-row no-gutters v-else>
							<v-file-input v-model="logo"
										  counterlabel="File input"
										  placeholder="Select project Logo"
										  prepend-icon="mdi-paperclip"
										  dense
										  accept="image/*"
										  show-size />
						</v-row>
					</v-tab-item>

					<v-tab-item class="pt-4">
						<v-card outlined>
							<v-card-text>
								<v-switch v-model="formProject.MobileAccessEnabled"
										  label="Iris Mobile is enabled for this project"
										  hide-details
										  class="mt-0 mb-2" />
								<p class="caption mb-4">
									After the end date Iris Mobile stops working on
									every phone assigned to this project. Leave the
									dates empty for a project that never expires.
								</p>
								<v-row>
									<v-col cols="12" md="6">
										<v-menu v-model="showValidFromPicker"
												:close-on-content-click="false"
												transition="scale-transition"
												offset-y
												min-width="auto">
											<template v-slot:activator="{ on, attrs }">
												<v-text-field v-model="validFromDate"
															  label="Project starts on"
															  prepend-icon="mdi-calendar-start"
															  clearable
															  readonly
															  v-bind="attrs"
															  v-on="on" />
											</template>
											<v-date-picker v-model="validFromDate"
														   @input="showValidFromPicker = false" />
										</v-menu>
									</v-col>
									<v-col cols="12" md="6">
										<v-menu v-model="showValidUntilPicker"
												:close-on-content-click="false"
												transition="scale-transition"
												offset-y
												min-width="auto">
											<template v-slot:activator="{ on, attrs }">
												<v-text-field v-model="validUntilDate"
															  label="Project ends on"
															  prepend-icon="mdi-calendar-end"
															  clearable
															  readonly
															  :hint="mobileAccessHint"
															  persistent-hint
															  v-bind="attrs"
															  v-on="on" />
											</template>
											<v-date-picker v-model="validUntilDate"
														   :min="validFromDate"
														   @input="showValidUntilPicker = false" />
										</v-menu>
									</v-col>
								</v-row>
								<v-row>
									<v-col cols="12" md="6">
										<v-text-field v-model.number="formProject.MobileOfflineGraceDays"
													  type="number"
													  min="0"
													  label="Days the app may work offline"
													  hint="The app locks itself if it cannot reach the server for this long. 0 means it may not work offline at all."
													  persistent-hint />
									</v-col>
									<v-col cols="12" md="6">
										<v-text-field v-model.number="formProject.MobileSyncDrainDays"
													  type="number"
													  min="0"
													  label="Days to finish syncing after the project ends"
													  hint="Devices can still upload work they already captured, but cannot capture anything new."
													  persistent-hint />
									</v-col>
								</v-row>
								<v-divider class="my-2" />
								<v-row>
									<v-col cols="12" md="6">
										<v-text-field v-model="formProject.Password"
													  name="Password"
													  label="Database backup password"
													  hint="Iris Mobile encrypts the backup zip with this when a user exports the project database to a file."
													  persistent-hint
													  :type="showPassword ? 'text' : 'password'"
													  :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
													  @click:append="togglePasswordVisibility"
													  required />
									</v-col>
								</v-row>
							</v-card-text>
						</v-card>
						<mobile-access-admin v-if="formProject.Id && canManageMobileAccess"
											 :projectId="formProject.Id" />
						<p v-else-if="!formProject.Id" class="caption mt-4 mb-0">
							Save the project to generate unlock codes and see the
							phones connected to it.
						</p>
					</v-tab-item>
				</v-tabs-items>
			</v-form>
		</div>

		<v-spacer />
		<v-card-actions>
			<v-spacer />
			<v-btn text outlined @click="onCancel"> Cancel </v-btn>
			<v-btn color="primary" @click="onSubmit">
				<v-icon class="mr-2">mdi-content-save</v-icon>
				Save
			</v-btn>
		</v-card-actions>
		<db-connection v-if="showDialog"
					   :showDialog="showDialog"
					   :dbConnection="dbConnection"
					   @save="submitDbConnection"
					   @cancel="cancelDbConnection" />
	</div>
</template>
<script>
	import { ProjectManagementActions } from '../../store/actions';
	import {
		ProjectManagementGetters,
		ClientContextGetters,
	} from '../../store/getters';
	import { ProjectContextActions } from '../../store/actions';
	import { mapGetters } from 'vuex';
	import DbConnection from '../../components/shared/DbConnection.vue';
	import MobileAccessAdmin from './MobileAccessAdmin.vue';
	import { Permissions } from '../../enums/permissionsEnum';
	import ConfirmDialog from '@/components/core/dialogs/confirmDialog';
	import permissionsMixin from '../../mixins/permissionsMixin';
	import vueAppSubfolder from '../../utils/appSubfolderUtils';
	export default {
		name: 'AddEditProjectModal',
		components: { DbConnection, MobileAccessAdmin },
		mixins: [permissionsMixin],
		data() {
			return {
				logo: null,
				method: '',
				id: '',
				activeTab: 0,
				// The edited copy. Real data rather than a computed over the store: a computed
				// returns a plain object Vue never observes, so writing to it - which is what the
				// date pickers and the switch do - changed nothing on screen until a reload.
				formProject: {},
				showDialog: false,
				nameRules: [(v) => !!v || 'Name is required'],
				showPassword: false,
				showValidFromPicker: false,
				showValidUntilPicker: false,
			};
		},
		computed: {
			...mapGetters({
				selectedProject: [ProjectManagementGetters.GET_SELECTED_PROJECT],
				dbConnection: [ProjectManagementGetters.GET_DBCONNECTION],
				selectedClient: [ClientContextGetters.SELECTED_CLIENT],
			}),
			showDbButton() {
				if (this.dbConnection) return false;
				return true;
			},
			odataUrl() {
				let param = this.selectedClient.Host;
				if (vueAppSubfolder.get() && vueAppSubfolder.get() != '/')
					param = param + vueAppSubfolder.get();
				return `https://${param}/mobilemodule/projects/${this.$route.params.id}/ODATA`;
			},
			swaggerUrl() {
				let param = this.selectedClient.Host;
				if (vueAppSubfolder.get() && vueAppSubfolder.get() != '/')
					param = param + vueAppSubfolder.get();
				return `https://${param}/MobileModule/swagger/index.html`;
			},
			canViewSwaggerUrl() {
				return this.hasPermissions([
					Permissions.ProjectManagement.CanViewSwaggerUrl,
				]);
			},
			canViewOdataUrl() {
				return this.hasPermissions([
					Permissions.ProjectManagement.CanViewOdataUrl,
				]);
			},
			canViewProjectId() {
				return this.hasPermissions([
					Permissions.ProjectManagement.CanViewProjectId,
				]);
			},
			canManageMobileAccess() {
				return this.hasPermissions([
					Permissions.ProjectManagement.ManageMobileAccess,
				]);
			},
			// The pickers work in whole days; the stored values are timestamps. An end date has to
			// cover the whole of that day, otherwise "ends on the 31st" would cut users off at midnight.
			validFromDate: {
				get() {
					return this.toDatePart(this.formProject.ValidFrom);
				},
				set(value) {
					this.formProject.ValidFrom = value
						? `${value}T00:00:00Z`
						: null;
				},
			},
			validUntilDate: {
				get() {
					return this.toDatePart(this.formProject.ValidUntil);
				},
				set(value) {
					this.formProject.ValidUntil = value
						? `${value}T23:59:59Z`
						: null;
				},
			},
			mobileAccessHint() {
				if (!this.formProject.MobileAccessEnabled)
					return 'Iris Mobile is switched off for this project.';
				if (!this.validUntilDate) return 'This project never expires.';
				return `Devices keep working until the end of ${this.validUntilDate}.`;
			},
			loginTest: {
				get() {
					if (this.selectedProject && this.selectedProject.LoginMethod) {
						return this.loginItems.find(
							(x) => x.id == this.selectedProject.LoginMethod
						).id;
					}
					return null;
				},
				set(val) {
					this.formProject.LoginMethod = val;
				},
			},
		},
		watch: {
			// Reloading the project - opening one, saving one, generating an API key - replaces the
			// copy being edited. Assigning a whole new object is what makes its fields reactive;
			// mutating one in place would leave any field the payload happened to omit invisible to
			// Vue for the life of the page.
			selectedProject: {
				immediate: true,
				handler(value) {
					this.formProject = Object.assign({}, this.mobileDefaults(), value);
				},
			},
		},
		destroyed() {
			this.$store.dispatch(ProjectManagementActions.CLEAR_SELECTEDPROJECT);
		},
		mounted() {
			if (this.$route.params.id) {
				this.id = this.$route.params.id;
				this.$store
					.dispatch(
						ProjectManagementActions.LOAD_DATA_PROJECTID,
						this.$route.params.id
					)
					.then(() => { });
			}
		},
		methods: {
			// What a project that has never been saved starts with. These are the same values the
			// API fills in when a payload omits them, so the form shows what will actually be
			// stored; they also have to exist as properties before Vue will track edits to them.
			mobileDefaults() {
				return {
					MobileAccessEnabled: true,
					MobileOfflineGraceDays: 7,
					MobileSyncDrainDays: 14,
					ValidFrom: null,
					ValidUntil: null,
				};
			},
			getProjectLogoURL(image) {
				let path =
					window.location.origin +
					(process.env.VUE_APP_SUBFOLDER === '/'
						? ''
						: process.env.VUE_APP_SUBFOLDER) +
					'/uploads';
				path = path + image;
				return path;
			},
			deleteProjectLogo() {
				ConfirmDialog({
					message: 'Are you sure you want to delete this logo?',
				}).then((isDelete) => {
					if (!isDelete) return;
					this.$store.dispatch(
						ProjectManagementActions.REMOVE_LOGO,
						this.formProject
					);
				});
			},
			createIntegrationApiKey() {
				this.$store.dispatch(
					ProjectManagementActions.CREATE_INTEGRATION_API_LEY,
					this.$route.params.id
				);
			},
			copyToClipboard(text) {
				navigator.clipboard.writeText(text);
			},
			submitDbConnection(item) {
				item.ProjectId = this.selectedProject.Id;
				if (item.Id) {
					this.$store
						.dispatch(ProjectManagementActions.UPDATE_DBCON, item)
						.then(() => {
							this.showDialog = false;
							this.loadData();
						});
				} else {
					this.$store
						.dispatch(
							ProjectManagementActions.LOAD_DB_BYCLIENTNAME,
							item.DatabaseName
						)
						.then((resp) => {
							if (resp)
								this.$store
									.dispatch(
										ProjectManagementActions.INSERT_DBCON,
										item
									)
									.then(() => {
										this.$store.dispatch(
											ProjectContextActions.RELOAD_PROJECT_CONTEXT
										);
										this.showDialog = false;
										this.loadData();
									});
						});
				}
			},
			cancelDbConnection() {
				this.showDialog = false;
			},
			dbConnectionSetup() {
				this.showDialog = true;
			},
			togglePasswordVisibility() {
				this.showPassword = !this.showPassword;
			},
			toDatePart(value) {
				return value ? String(value).substring(0, 10) : null;
			},
			onSubmit() {
				// The required fields all live on the first tab, so a failed validation
				// would otherwise mark fields the user cannot see.
				if (!this.$refs.form.validate()) {
					this.activeTab = 0;
					return;
				}
				this.formProject.ClientId = Number(this.formProject.ClientId);
				if (this.formProject.Id) {
					this.$store.dispatch(ProjectManagementActions.UPDATE, {
						item: this.formProject,
						logo: this.logo,
					});
				} else {
					this.$store
						.dispatch(ProjectManagementActions.INSERT_PROJECT, {
							item: this.formProject,
							logo: this.logo,
						})
						.then(() => {
							let id = this.selectedProject.Id;
							this.$router.push({
								name: 'ProjectsManagementEdit',
								params: { id: id },
							});
						});
				}
			},
			onCancel() {
				this.$emit('cancel');
				this.$router.push({
					name: 'Projects',
				});
			},
			loadData() {
				this.$store.dispatch(
					ProjectManagementActions.LOAD_DATA_PROJECTID,
					this.$route.params.id
				);
			},
		},
	};
</script>
